import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ReactFlow, { Controls, PanOnScrollMode, ReactFlowProvider } from 'reactflow';
import { useIsMutating } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { faArrowRight, faClose, faPen, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AutoSaveStatusIndicator } from 'assets/js/pages/engage/workflows/components/AutoSaveStatusIndicator';
import { BuilderAlerts } from 'assets/js/pages/engage/workflows/components/BuilderAlerts';
import * as BuilderFrame from 'assets/js/pages/engage/workflows/components/BuilderFrame';
import { ConflictingTriggersPanel } from 'assets/js/pages/engage/workflows/components/ConflictingTriggersPanel';
import * as EditWorkflowNameDialog from 'assets/js/pages/engage/workflows/components/EditWorkflowNameDialog';
import { NoAuctionsPanel } from 'assets/js/pages/engage/workflows/components/NoAuctionsPanel';
import { SmallBrowserAlertDialog } from 'assets/js/pages/engage/workflows/components/SmallBrowserAlertDialog';
import { UndoRedoPanel } from 'assets/js/pages/engage/workflows/components/UndoRedoPanel';
import { UnpublishedChangesPanel } from 'assets/js/pages/engage/workflows/components/UnpublishedChangesPanel';
import { WorkflowStatusBadge } from 'assets/js/pages/engage/workflows/components/WorkflowStatusBadge';
import { useAutosaveNewNodes } from 'assets/js/pages/engage/workflows/hooks/useAutosaveNewNodes';
import { useCustomFieldFilterItems } from 'assets/js/pages/engage/workflows/hooks/useCustomFieldFilterItems';
import { useLayout } from 'assets/js/pages/engage/workflows/hooks/useLayout';
import { useSetViewportCenter } from 'assets/js/pages/engage/workflows/hooks/useSetViewportCenter';
import { utilityNodeTypes } from 'assets/js/pages/engage/workflows/utils/constants';
import { edgeTypes } from 'assets/js/pages/engage/workflows/utils/edgeTypes';
import {
    setRemoteMessageIdsOnNodes,
    setRemoteStepIdsOnNodes,
} from 'assets/js/pages/engage/workflows/utils/helpers';
import { nodeTypes } from 'assets/js/pages/engage/workflows/utils/nodeTypes';
import {
    actionsHaveFormDataSelector,
    autoSavedSelector,
    nodeFormErrorsSelector,
    useReactFlowStore,
    workflowHasAction,
    workflowHasActionSelector,
    workflowHasBranch,
} from 'assets/js/pages/engage/workflows/utils/store';
import { transformStepsToNodes } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import * as Tooltip from 'components/tooltip';
import { EdgeTypeEnum, WorkflowUtilityNodeTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { useCurrentWorkflowSteps } from 'customHooks/useCurrentWorkflowSteps';
import { filterUndefined } from 'helpers/arrays';
import { difference, last } from 'lodash';
import { queryClient } from 'queries/queryClient';
import { workflowMutationKeys, workflowQueryKeys } from 'queries/workflow-query-keys';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

const EditWorkflowBuilder = () => {
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { data: workflow, isRefetching: isRefetchingWorkflow } = useCurrentWorkflow();
    const { isRefetching: isRefetchingWorkflowSteps } = useCurrentWorkflowSteps({
        version: workflow.has_draft_version
            ? workflow.current_version + 1
            : workflow.current_version,
    });
    const isRefetchingWorkflowOrSteps = isRefetchingWorkflow || isRefetchingWorkflowSteps;

    const { reset: resetReactFlowStore } = useReactFlowStore(({ reset }) => ({ reset }));
    const { errorCount } = useReactFlowStore(useShallow(nodeFormErrorsSelector));
    const workflowHasAction = useReactFlowStore(workflowHasActionSelector);
    const actionsHaveFormData = useReactFlowStore(actionsHaveFormDataSelector);
    const { autoSaveStatus } = useReactFlowStore(autoSavedSelector);

    const isSaving =
        useIsMutating({
            mutationKey: workflowMutationKeys.workflowAutoSave(),
        }) > 0;

    useAutosaveNewNodes();

    useEffect(() => {
        if (isRefetchingWorkflowOrSteps) {
            resetReactFlowStore();
        }
    }, [isRefetchingWorkflowOrSteps, resetReactFlowStore]);

    return (
        <>
            <SmallBrowserAlertDialog />
            <BuilderFrame.Root>
                <BuilderFrame.Title>Editing workflow: {workflow.name}</BuilderFrame.Title>
                <BuilderFrame.Header>
                    <div className="flex items-center gap-6">
                        <Button
                            onClick={() => {
                                history.push(`/accounts/${accountId}/workflows/${workflow.id}`);
                            }}
                            variant="transparent"
                            size="sm"
                            shape="square"
                            color="neutral"
                            aria-label="Go back"
                        >
                            <FontAwesomeIcon icon={faClose} size="lg" className="text-gray-600" />
                        </Button>

                        <div className="flex items-center gap-6">
                            {workflow.status === 'active' ? (
                                <WorkflowStatusBadge status={workflow.status} />
                            ) : null}
                            <div className="flex items-center">
                                <div className="flex items-center gap-2">
                                    {workflow.icon ? <span>{workflow.icon}</span> : null}
                                    <span className="text-lg font-medium">{workflow.name}</span>
                                </div>

                                <EditWorkflowNameDialog.Root>
                                    <EditWorkflowNameDialog.Trigger asChild>
                                        <Button
                                            variant="transparent"
                                            size="sm"
                                            shape={'square'}
                                            aria-label="Edit workflow name"
                                        >
                                            <FontAwesomeIcon
                                                icon={faPen}
                                                size="sm"
                                                className="text-gray-600"
                                            />
                                        </Button>
                                    </EditWorkflowNameDialog.Trigger>
                                </EditWorkflowNameDialog.Root>
                            </div>
                        </div>
                    </div>
                    <div className="flex w-full items-center justify-center gap-2 md:w-auto">
                        <AutoSaveStatusIndicator />
                        <RefreshButton isRefetching={isRefetchingWorkflowOrSteps} />
                        <BuilderAlerts />

                        {errorCount > 0 ||
                        isSaving ||
                        !workflowHasAction ||
                        !actionsHaveFormData ? (
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <Button variant="solid" disabled>
                                        {workflow.has_draft_version ? 'Publish changes' : 'Next'}
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content side="bottom" align="end" inverse>
                                        <Tooltip.Arrow inverse />
                                        <p className="max-w-36 text-sm">
                                            Finish your workflow before continuing.
                                        </p>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        ) : (
                            <Button
                                variant="solid"
                                onClick={() => history.push('./review')}
                                disabled={autoSaveStatus !== 'success'}
                            >
                                {workflow.has_draft_version ? 'Publish changes' : 'Next'}
                                <FontAwesomeIcon icon={faArrowRight} />
                            </Button>
                        )}
                    </div>
                </BuilderFrame.Header>

                <BuilderFrame.Content>
                    {isRefetchingWorkflowOrSteps ? (
                        <Loader variant="absolute" />
                    ) : (
                        <ReactFlowProvider>
                            <EditWorkflowBuilderCanvas />
                        </ReactFlowProvider>
                    )}
                </BuilderFrame.Content>
            </BuilderFrame.Root>
        </>
    );
};

interface RefreshButtonProps {
    isRefetching: boolean;
}
const RefreshButton = ({ isRefetching }: RefreshButtonProps) => {
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();

    return (
        <Button
            variant="outline"
            color={'neutral'}
            shape={'square'}
            disabled={isRefetching}
            onClick={() => {
                queryClient.invalidateQueries({
                    queryKey: workflowQueryKeys.workflowById(accountId, workflow.id),
                });
            }}
            aria-label="Refresh workflow"
        >
            <FontAwesomeIcon className={twMerge(isRefetching && 'animate-spin')} icon={faRefresh} />
        </Button>
    );
};

const EditWorkflowBuilderCanvas = () => {
    const { state } = useLocation<{ selectedNodeId?: string }>();
    const { data: customFieldOptions } = useCustomFieldFilterItems();
    const { data: workflow } = useCurrentWorkflow();
    const { data: workflowSteps } = useCurrentWorkflowSteps({
        version: workflow.has_draft_version
            ? workflow.current_version + 1
            : workflow.current_version,
    });

    const hasSetInitialData = useRef<boolean>(false);

    const { nodes, edges, onNodesChange, onEdgesChange, getNodes, setNodes, updateNode } =
        useReactFlowStore(
            ({ nodes, edges, onNodesChange, onEdgesChange, getNodes, setNodes, updateNode }) => ({
                nodes,
                edges,
                onNodesChange,
                onEdgesChange,
                getNodes,
                setNodes,
                updateNode,
            }),
        );

    useSetViewportCenter();
    useLayout();

    useEffect(() => {
        const nodesFromApi = transformStepsToNodes({
            steps: workflowSteps,
            workflow,
            customFieldOptions,
        });

        if (hasSetInitialData.current) {
            const localNodes = getNodes().filter(
                n => !utilityNodeTypes.includes(n.type! as WorkflowUtilityNodeTypeEnum),
            );

            setRemoteStepIdsOnNodes(localNodes, nodesFromApi, updateNode);
            setRemoteMessageIdsOnNodes(localNodes, nodesFromApi, updateNode);
        } else {
            const newNodes: NodeWithData[] = [
                ...nodesFromApi.map(node =>
                    node.id === state?.selectedNodeId ? { ...node, selected: true } : node,
                ),
            ];
            const hasAction = workflowHasAction(nodesFromApi);
            const hasBranch = workflowHasBranch(nodesFromApi);
            const parentId = last(newNodes)?.id ?? null;

            if (!hasAction && !hasBranch) {
                // if we don't have an action yet, go ahead and insert CHOOSE_ACTION type
                newNodes.push({
                    id: uuid(),
                    data: { type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION, parentId },
                    type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
                    position: { x: 0, y: 0 },
                    style: { opacity: 0 },
                    selected: true,
                });
            } else {
                // otherwise, insert LEAF_END types at each leaf termination
                const allParentIds = filterUndefined(newNodes.map(node => node.data?.parentId));
                const allNodeIds = newNodes.map(node => node.id);
                const terminalLeafIds = difference(allNodeIds, allParentIds);

                for (const terminalLeafId of terminalLeafIds) {
                    newNodes.push({
                        id: uuid(),
                        data: {
                            type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                            parentId: terminalLeafId,
                        },
                        type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                        position: { x: 0, y: 0 },
                        style: { opacity: 0 },
                        selectable: false,
                    });
                }
            }

            setNodes(newNodes);
            hasSetInitialData.current = true;
        }
    }, [workflow, workflowSteps, customFieldOptions, state, getNodes, setNodes, updateNode]);

    return (
        <div className="relative flex h-full w-full items-center justify-center bg-gray-100 shadow-inner">
            <ReactFlow
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                proOptions={{ hideAttribution: true }}
                fitView={true}
                fitViewOptions={{ maxZoom: 1, padding: 0.2 }}
                panOnScroll={true}
                panOnScrollMode={PanOnScrollMode.Free}
                zoomOnScroll={false}
                nodesDraggable={false}
                autoPanOnNodeDrag={false}
                zoomOnDoubleClick={false}
                deleteKeyCode={null}
                selectionKeyCode={null}
                multiSelectionKeyCode={null}
                defaultEdgeOptions={{ type: EdgeTypeEnum.BASE, focusable: false }}
            >
                <UndoRedoPanel />
                <UnpublishedChangesPanel />
                <ConflictingTriggersPanel />
                <NoAuctionsPanel />
                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );
};

export { EditWorkflowBuilder };
